import { CSSProperties, useCallback, useMemo, useRef } from "react";

import TableContextProvider from "components/Table/Context";
import Table from "components/Table";
import TableVirtualizedSpacerRow from "components/Table/VirtualizedSpacerRow";
import TableRow from "components/Table/Row";
import useTableSort from "components/Table/useTableSort";
import TableWrapper from "components/Table/Wrapper";
import EmptyState from "ds/components/EmptyState";
import {
  EmptystateSearchNoResultsColored,
  EmptystateUsedByColored,
} from "components/icons/generated";
import SearchInput from "components/SearchInput";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import PageInfoHeader from "ds/components/PageInfoHeader";

import RoleUsedByListItem from "./ListItem";
import {
  FILTERS_ORDER_SETTINGS_KEY,
  INITIAL_COLUMNS_VIEW_CONFIG,
  initialSortDirection,
  initialSortOption,
  USED_BY_COLUMN_CONFIG,
  USED_BY_COLUMNS_STORAGE_KEY,
  USED_BY_CUSTOM_END_COLUMN_CONFIG,
  ROW_HEADER_ID,
  SORT_CONFIG,
} from "./constants";
import { ANALYTICS } from "../constants";
import { useFilteredEntities } from "./useFilteredEntities";
import { EntityWithRoleBinding } from "./types";
import { getEntities } from "./helpers";
import { RoleView } from "../types";

type RoleUsedByProps = {
  role: RoleView;
  readOnly?: boolean;
};

const RoleUsedBy = ({ role, readOnly }: RoleUsedByProps) => {
  const virtualizedListContainerRef = useRef<HTMLDivElement | null>(null);

  const { handleSortOptionChange, sortDescriptor } = useTableSort({
    filtersOrderSettingsKey: FILTERS_ORDER_SETTINGS_KEY,
    sortConfig: SORT_CONFIG,
    initialSortOption,
    initialSortDirection,
  });

  const entities = useMemo(() => getEntities(role.roleBindings), [role.roleBindings]);

  const renderRow = useCallback(
    (
      item: Partial<EntityWithRoleBinding> & {
        virtIndex?: number;
        style?: CSSProperties;
        virtKey?: string;
        id: string;
        height?: number;
        ref?: (node: Element | null | undefined) => void;
      }
    ) => {
      if ("height" in item && (item.id === "after" || item.id === "before")) {
        return <TableVirtualizedSpacerRow height={item.height} />;
      }

      return (
        <TableRow
          index={item.virtIndex}
          ref={item.ref}
          style={item.style}
          key={item.virtKey}
          id={item.id}
          name={item.id}
        >
          <RoleUsedByListItem
            entity={item as EntityWithRoleBinding}
            roleName={role.name}
            readOnly={readOnly}
          />
        </TableRow>
      );
    },
    [readOnly, role.name]
  );

  const filteredEntities = useFilteredEntities(entities, sortDescriptor);

  return (
    <>
      <PageInfoHeader title="Used by">
        <SearchInput placeholder={SEARCH_PLACEHOLDERS.ROLE_USED_BY} filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY} />
      </PageInfoHeader>
      <TableContextProvider
        columnsConfig={USED_BY_COLUMN_CONFIG}
        endCustomColumns={USED_BY_CUSTOM_END_COLUMN_CONFIG}
        initialColumnViewConfig={INITIAL_COLUMNS_VIEW_CONFIG}
        rowHeaderId={ROW_HEADER_ID}
        localStorageId={USED_BY_COLUMNS_STORAGE_KEY}
        setSortDescriptor={handleSortOptionChange}
        sortDescriptor={sortDescriptor}
        {...ANALYTICS}
      >
        {!!filteredEntities?.length && (
          <TableWrapper virtualizedListContainerRef={virtualizedListContainerRef}>
            <Table ariaLabel="Used by" items={filteredEntities} ref={virtualizedListContainerRef}>
              {renderRow}
            </Table>
          </TableWrapper>
        )}
        {!filteredEntities.length && !entities?.length && (
          <EmptyState title="You don’t have any assignments yet" icon={EmptystateUsedByColored} />
        )}
        {!filteredEntities.length && !!entities?.length && (
          <EmptyState title="No results" icon={EmptystateSearchNoResultsColored} announce />
        )}
      </TableContextProvider>
    </>
  );
};

export default RoleUsedBy;
